import { Package, ShoppingCart, Receipt, ArrowDownToLine } from 'lucide-react';
import { round } from '@/utils/mathUtils';
import { memo } from 'react';

import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface.ts';
import { formatNumberWithCommas } from '@/utils/FormatNumber';

type SummarySectionProps = {
  items: IOneItemInPrepareForRequest[];
  itemsInRequest: IOneItemInPrepareForRequest[];
  requestNotes: string;
  handleRequestNotes: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isDisabled: boolean;
};

// Memoized SummaryItem component to reduce re-renders
const SummaryItem = memo(
  ({
    label,
    value,
    icon: Icon,
    tooltipText,
  }: {
    label: string;
    value: React.ReactNode;
    icon: React.ElementType;
    tooltipText?: string;
  }) => (
    <div className="flex items-center gap-1.5 rounded-md p-1.5 transition-colors hover:bg-muted/50">
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-purple-500/10 to-indigo-500/10 ring-1 ring-purple-500/20">
        <Icon className="h-3 w-3 text-purple-500" />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <span className="cursor-help text-[10px] text-muted-foreground">
                  {label}
                </span>
              </TooltipTrigger>
              {tooltipText && (
                <TooltipContent side="top" className="max-w-[200px] text-xs">
                  {tooltipText}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <span className="text-xs font-medium">{value}</span>
        </div>
      </div>
    </div>
  ),
);

SummaryItem.displayName = 'SummaryItem';

export const SummarySection = memo(
  ({
    items,
    itemsInRequest,
    requestNotes,
    handleRequestNotes,
    isDisabled,
  }: SummarySectionProps) => {
    // Calculate total amount using memoization from the round function directly
    const totalAmount = round(
      itemsInRequest?.reduce(
        (sum, x) => (x?.TotalAfterDiscount || 0) + sum,
        0,
      ) as number,
      2,
    );

    return (
      <div className="space-y-2 rounded-md bg-gradient-to-br from-purple-500/5 via-indigo-500/10 to-purple-500/5 p-2 shadow-inner">
        <Card className="border-purple-200/30 bg-white/70 p-1 shadow-sm dark:border-purple-800/30 dark:bg-black/20">
          <div className="space-y-0.5">
            <SummaryItem
              label="أصناف العرض"
              value={`${items.length} صنف`}
              icon={Package}
              tooltipText="إجمالي عدد الأصناف المتاحة في العرض"
            />
            <SummaryItem
              label="أصناف الطلبية"
              value={`${itemsInRequest.length} صنف`}
              icon={ShoppingCart}
              tooltipText="عدد الأصناف المحددة في الطلبية"
            />
            <SummaryItem
              label="إجمالي الطلبية"
              value={`${formatNumberWithCommas(totalAmount)} ج.م`}
              icon={Receipt}
              tooltipText="القيمة الإجمالية للطلبية بعد الخصم"
            />
          </div>
        </Card>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label
              htmlFor="request-notes"
              className="flex items-center gap-1.5 text-[10px] text-muted-foreground"
            >
              <ArrowDownToLine className="h-3 w-3 text-purple-500" />
              ملاحظات على الطلبية
            </label>
            {isDisabled && (
              <span className="rounded-sm bg-purple-100 px-1.5 py-0.5 text-[9px] text-purple-700 dark:bg-purple-900/20 dark:text-purple-300">
                الطلبية مكتملة
              </span>
            )}
          </div>
          <Textarea
            id="request-notes"
            disabled={isDisabled}
            className={cn(
              'min-h-[60px] w-full resize-none rounded-md border-input bg-background text-xs placeholder:text-muted-foreground/50',
              isDisabled &&
                'cursor-not-allowed bg-muted/50 text-muted-foreground',
            )}
            placeholder="أضف ملاحظاتك هنا..."
            value={requestNotes}
            onChange={handleRequestNotes}
          />
        </div>
      </div>
    );
  },
);

SummarySection.displayName = 'SummarySection';
